import { useAtomValue } from "@effect/atom-react";
import { createEnvironmentRpcSubscriptionAtomFamily } from "@amoreg/client-runtime/state/runtime";
import { type ComplianceStreamEvent, WS_METHODS } from "@amoreg/contracts";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schedule from "effect/Schedule";
import * as Stream from "effect/Stream";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { connectionAtomRuntime } from "../connection/runtime";
import { primaryEnvironmentIdAtom } from "./primaryEnvironment";

const subscription = createEnvironmentRpcSubscriptionAtomFamily(connectionAtomRuntime, {
  label: "web-amoreg:compliance-live",
  tag: WS_METHODS.subscribeCompliance,
  idleTtlMs: 0,
  // A directory outage denies delivery, but must not leave this page stale
  // forever once access can be verified again. Transport recovery stays in
  // the connection supervisor; domain failures retry at a bounded cadence.
  transform: (stream) => stream.pipe(Stream.retry(Schedule.spaced("15 seconds"))),
});

/** Shared by every read for this organization; follows the existing reconnect supervisor. */
export const complianceEventAtom = Atom.family((organizationId: string) =>
  Atom.make((get) => {
    const environmentId = get(primaryEnvironmentIdAtom);
    if (!environmentId || !organizationId) return null;
    const result = get(subscription({ environmentId, input: { organizationId } }));
    const value = AsyncResult.value(result);
    if (value._tag === "None" || value.value.organizationId !== organizationId) return null;
    return value.value;
  }).pipe(Atom.withEquality<ComplianceStreamEvent | null>((previous, next) => previous === next ||
    (previous !== null && next !== null && previous.organizationId === next.organizationId &&
      previous.revision === next.revision && previous.type === next.type))),
);

export const complianceRevisionAtom = Atom.family((organizationId: string) =>
  Atom.make((get) => get(complianceEventAtom(organizationId))?.revision ?? null),
);

const runtime = Atom.runtime(Layer.empty);

/**
 * How long a compliance read outlives its last subscriber.
 *
 * Without this, every read was disposed the moment its page unmounted and
 * re-fetched from scratch on the next mount — so moving between compliance
 * sections, or a page remounting while it settled, cost the full batch of
 * requests again and showed a skeleton over data the browser had a moment
 * ago. Kept alive, a read is served from memory on the next visit, and the
 * organization's compliance stream (which the read depends on) stays
 * subscribed for the same window, so a change made meanwhile still
 * revalidates it. Five minutes covers a working session's navigation
 * without holding an abandoned organization's data for long.
 */
export const COMPLIANCE_READ_IDLE_TTL_MS = 5 * 60_000;

/**
 * How long a read mounted before the organization's stream has reported
 * anything waits for the snapshot before going ahead without it.
 *
 * Such a read used to go out at once and again the moment the snapshot
 * arrived, so a fresh page paid for its whole batch twice — and the abandoned
 * requests still ran to completion on the API. The snapshot normally lands
 * within about 100 ms of subscribing; waiting up to this long for it collapses
 * the two into one read. Without a usable stream (no environment yet, delivery
 * denied, transport down) the read still goes out once the grace passes, so no
 * page depends on the stream to show data.
 */
export const FIRST_EVENT_GRACE_MS = 750;

const afterFirstEvent = <A, E>(event: ComplianceStreamEvent | null, read: Effect.Effect<A, E>) =>
  event === null ? read.pipe(Effect.delay(FIRST_EVENT_GRACE_MS)) : read;

/** Tag reads with the triggering event so notifications cannot race a reconnect. */
export function complianceEventQueryAtom<A, E>(organizationId: string, read: (event: ComplianceStreamEvent | null) => Effect.Effect<A, E>) {
  return runtime
    .atom((get) => {
      const event = get(complianceEventAtom(organizationId));
      return afterFirstEvent(event, read(event));
    })
    .pipe(Atom.setIdleTTL(COMPLIANCE_READ_IDLE_TTL_MS));
}

/**
 * Revalidates cached and mounted org reads together, preserving prior data
 * while loading, and keeps the result for {@link COMPLIANCE_READ_IDLE_TTL_MS}
 * after the last subscriber leaves.
 */
export function complianceQueryAtom<A, E>(organizationId: string, read: Effect.Effect<A, E>) {
  return runtime
    .atom((get) => afterFirstEvent(get(complianceEventAtom(organizationId)), read))
    .pipe(Atom.setIdleTTL(COMPLIANCE_READ_IDLE_TTL_MS));
}

/** For imperative readers such as inspection transcripts outside the governance atoms. */
export function useComplianceRevision(organizationId: string | null): string | null {
  return useAtomValue(complianceRevisionAtom(organizationId ?? ""));
}
